import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ChartLine, Users, ShoppingCart, CurrencyInr, Lock } from '@phosphor-icons/react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { BACKEND_URL } from '../config';

const SharedDashboard = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const { data: result } = await axios.get(`${BACKEND_URL}/api/shared/${token}`);
      setData(result);
    } catch (err) {
      setError(err.response?.data?.detail || 'Dashboard not found or link expired');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <div className="text-lg">Loading shared dashboard...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-foreground">
        <div className="text-center">
          <Lock size={48} weight="thin" className="mx-auto mb-4 opacity-50" />
          <div className="text-xl font-semibold mb-2">Dashboard Unavailable</div>
          <div className="text-sm text-muted-foreground mb-6">{error}</div>
          <Link to="/login" className="text-sm text-accent hover:underline">Sign in to your dashboard</Link>
        </div>
      </div>
    );
  }

  const formatMonth = (label) => {
    if (!label) return '';
    const [y, m] = label.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${months[parseInt(m)-1]} '${y.slice(2)}`;
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="text-xl font-bold tracking-tight" data-testid="shared-dashboard-title">
              {data.owner_name ? `${data.owner_name}'s Dashboard` : 'Shared Dashboard'}
            </div>
            <div className="text-xs text-muted-foreground tracking-[0.2em] uppercase">READ-ONLY VIEW</div>
          </div>
          <Link to="/login" className="text-sm text-accent hover:underline">Sign In</Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-card border border-border rounded-sm p-6" data-testid="shared-total-revenue">
            <div className="flex items-center gap-2 mb-2">
              <CurrencyInr size={16} weight="regular" className="text-muted-foreground" />
              <span className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Total Revenue</span>
            </div>
            <div className="text-3xl font-bold tracking-tight">₹{data.total_revenue.toLocaleString()}</div>
          </div>
          <div className="bg-card border border-border rounded-sm p-6" data-testid="shared-active-clients">
            <div className="flex items-center gap-2 mb-2">
              <Users size={16} weight="regular" className="text-muted-foreground" />
              <span className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Active Clients</span>
            </div>
            <div className="text-3xl font-bold tracking-tight">{data.active_clients}</div>
          </div>
          <div className="bg-card border border-border rounded-sm p-6" data-testid="shared-transactions">
            <div className="flex items-center gap-2 mb-2">
              <ShoppingCart size={16} weight="regular" className="text-muted-foreground" />
              <span className="text-xs tracking-[0.15em] uppercase font-bold text-muted-foreground">Transactions</span>
            </div>
            <div className="text-3xl font-bold tracking-tight">{data.total_transactions}</div>
          </div>
        </div>

        {/* Revenue chart */}
        <div className="bg-card border border-border rounded-sm p-6">
          <div className="flex items-center gap-2 mb-6">
            <ChartLine size={20} weight="regular" />
            <span className="font-semibold">Revenue Trend</span>
          </div>
          {data.chart_data.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={data.chart_data}>
                <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                <XAxis dataKey="month" tickFormatter={formatMonth} stroke="hsl(var(--foreground))" style={{ fontSize: '12px' }} />
                <YAxis stroke="hsl(var(--foreground))" style={{ fontSize: '12px' }} tickFormatter={(v) => `₹${(v/100000).toFixed(0)}L`} />
                <Tooltip formatter={(v) => `₹${v.toLocaleString()}`} contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '0.25rem' }} />
                <Line type="monotone" dataKey="revenue" stroke="hsl(var(--accent))" strokeWidth={2.5} dot={{ fill: 'hsl(var(--accent))', r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-48 flex items-center justify-center text-muted-foreground">No data available</div>
          )}
        </div>

        {/* Top clients */}
        <div className="bg-card border border-border rounded-sm">
          <div className="p-5 border-b border-border font-semibold text-sm">Top Clients</div>
          <div className="divide-y divide-border">
            {data.top_clients.map((c, i) => (
              <div key={i} data-testid={`shared-client-${i}`} className="flex items-center justify-between px-5 py-3">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-muted flex items-center justify-center text-xs font-bold">{i + 1}</span>
                  <span className="text-sm font-medium">{c.name}</span>
                </div>
                <span className="text-sm font-semibold">₹{c.revenue.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="text-center text-xs text-muted-foreground py-4">
          This is a read-only shared view. <Link to="/register" className="text-accent hover:underline">Create your own dashboard</Link>
        </div>
      </main>
    </div>
  );
};

export default SharedDashboard;
